import React, { FC, PropsWithChildren, ReactNode, useCallback, useLayoutEffect, useRef, useState } from "react";
import { BaseProps } from "../../BaseProps";
import { Icon } from "../Icon";
import classNames from "classnames";
import { MessageAppearance, Messages } from "./MessageAppearances";
import { StyledShelf, StyledStack } from "./StyledMessage";
import { Shelf, ShelfProps, Stack, StackProps } from "../../layout";

/**
 * The `Message` component properties.
 */
export interface MessageProps extends BaseProps {

  /**
   * The message appearance.
   */
  readonly appearance?: MessageAppearance;

  /**
   * Indicates whether the message is visible, if not specified defaults to `false`.
   */
  readonly visible?: boolean;

  /**
   * The message content.
   */
  readonly children?: ReactNode;

  /**
   * The message action content.
   */
  readonly actionContent?: ReactNode;

  /**
   * The ARIA label for the Message, used for accessibility.
   */
  readonly "aria-label"?: string;
}

type LayoutProps = StackProps | ShelfProps;
type MessageContainerProps = PropsWithChildren<MessageProps & LayoutProps>;

/**
 * A component which displays a message.
 */
export const Message: FC<MessageProps> = (props) => {
  const { className, testId, style, appearance = Messages.Informational, visible = false, children, actionContent } = props;
  const { icon, iconColor } = appearance;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<number | null>(null);

  const updateWidth = useCallback((root: HTMLDivElement) => {
    setWidth(root.offsetWidth);
  }, []);

  useLayoutEffect(() => {
    const { current: root } = rootRef;
    if (!root) return;

    const observer = new ResizeObserver(() => updateWidth(root));
    observer.observe(root);
    updateWidth(root);

    return () => {
      observer.disconnect();
    };
  }, [updateWidth]);

  if (!visible) return null;

  const MessageContainer = ((width === null || width >= 500) ? StyledShelf : StyledStack) as React.ElementType<MessageContainerProps>;
  const ContentContainer = (width === null || width >= 500) ? Shelf : Stack;

  const getAriaLabel = (isError: boolean, isUrgent: boolean): string => {
    if (isError) return "Error message";
    if (isUrgent) return "Warning message";
    return "Success message";
  };

  const isError = appearance === Messages.Error;
  const isUrgent = isError || appearance === Messages.Warning;
  const ariaLabel = props["aria-label"] ?? getAriaLabel(isError, isUrgent);
  const ariaRole = isUrgent ? "alert" : "status";
  const ariaLive = isError ? "assertive" : "polite";

  return (
    <>
      {
        !actionContent ? (
          <div role={ariaRole} aria-live={ariaLive} aria-label={ariaLabel}>
            <MessageContainer
              appearance={appearance}
              className={classNames(className, "message")}
              spacing="small"
              testId={testId}
              style={style}>
              <Icon name={icon} color={iconColor} aria-hidden={true} data-testid="message-icon" />
              <div className="content">
                {children}
              </div>
            </MessageContainer>
          </div>
        ) : (
          <div ref={rootRef} role={ariaRole} aria-live={ariaLive} aria-label={ariaLabel}>
            <MessageContainer
              appearance={appearance}
              className={classNames(className, "message")}
              spacing="small"
              alignment="top"
              testId={testId}
              style={style}>
              <div className="re-orderable-content">
                <Icon name={icon} color={iconColor} aria-hidden={true} data-testid="message-icon" />
                <ContentContainer className="text-wrapper" spacing="small" alignment="span">
                  {children}
                  {
                    actionContent && actionContent
                  }
                </ContentContainer>
              </div>
            </MessageContainer>
          </div>
        )
      }
    </>
  );
};
